
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { createGroupUrl } from '../../../hooks/useNavigation';

interface Group {
  id: string;
  sequential_id: number;
  name: string;
  description: string;
  cover_image: string;
  member_count: number;
  category: string;
  privacy: string;
}

export default function FeaturedGroups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [userMemberships, setUserMemberships] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null);

  useEffect(() => {
    fetchFeaturedGroups();
  }, [user]);

  const fetchFeaturedGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('groups')
        .select('id, sequential_id, name, description, cover_image, member_count, category, privacy')
        .eq('privacy', 'public')
        .order('member_count', { ascending: false })
        .limit(5);

      if (error) throw error;
      setGroups(data || []);

      // Load user memberships
      if (user) {
        const { data: memberships } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id);

        if (memberships) {
          setUserMemberships(new Set(memberships.map(m => m.group_id)));
        }
      }
    } catch (error) {
      console.error('Error fetching featured groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (e: React.MouseEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || joiningGroupId) return;

    setJoiningGroupId(groupId);
    try {
      const isMember = userMemberships.has(groupId);

      if (isMember) {
        const { error } = await supabase
          .from('group_members')
          .delete()
          .eq('group_id', groupId)
          .eq('user_id', user.id);
        if (error) throw error;

        const group = groups.find(g => g.id === groupId);
        if (group) {
          await supabase
            .from('groups')
            .update({ member_count: Math.max(0, group.member_count - 1) })
            .eq('id', groupId);
        }

        setUserMemberships(prev => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
        setGroups(prev =>
          prev.map(g =>
            g.id === groupId ? { ...g, member_count: Math.max(0, g.member_count - 1) } : g
          )
        );
      } else {
        const { error } = await supabase
          .from('group_members')
          .insert({
            group_id: groupId,
            user_id: user.id,
            role: 'member',
            badge: null,
            level: 1,
          });
        if (error) throw error;

        const group = groups.find(g => g.id === groupId);
        if (group) {
          await supabase
            .from('groups')
            .update({ member_count: group.member_count + 1 })
            .eq('id', groupId);
        }

        setUserMemberships(prev => new Set(prev).add(groupId));
        setGroups(prev =>
          prev.map(g =>
            g.id === groupId ? { ...g, member_count: g.member_count + 1 } : g
          )
        );
      }
    } catch (error) {
      console.error('Error joining/leaving group:', error);
    } finally {
      setJoiningGroupId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Featured Groups</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (groups.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <span className="w-6 h-6 flex items-center justify-center text-teal-500 mr-2">
            <i className="ri-group-fill"></i>
          </span>
          Featured Groups
        </h3>
        <Link
          to="/groups"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
        >
          View All
        </Link>
      </div>

      <div className="space-y-3">
        {groups.map(group => {
          const isMember = userMemberships.has(group.id);
          const isJoining = joiningGroupId === group.id;
          const groupUrl = createGroupUrl(group.sequential_id, group.name);

          return (
            <div
              key={group.id}
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <Link to={groupUrl} className="relative flex-shrink-0 cursor-pointer">
                <img
                  src={group.cover_image}
                  alt={group.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  <i className="ri-group-line text-xs"></i>
                </div>
              </Link>
              <Link to={groupUrl} className="flex-1 min-w-0 cursor-pointer">
                <h4 className="font-semibold text-gray-900 text-sm group-hover:text-teal-600 transition-colors truncate">
                  {group.name}
                </h4>
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <i className="ri-user-line mr-1"></i>
                  <span>{group.member_count.toLocaleString()} members</span>
                  <span className="mx-2">&bull;</span>
                  <span className="capitalize">{group.category}</span>
                </div>
              </Link>
              {user ? (
                <button
                  onClick={e => handleJoinGroup(e, group.id)}
                  disabled={isJoining}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    isJoining
                      ? 'bg-gray-100 text-gray-400'
                      : isMember
                      ? 'bg-gray-100 text-gray-500 border border-gray-200'
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  {isJoining ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : isMember ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-line text-[10px]"></i>
                      Joined
                    </span>
                  ) : (
                    'Join'
                  )}
                </button>
              ) : (
                <Link to={groupUrl} className="flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-600 transition-colors"></i>
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <Link
        to="/groups"
        className="block mt-4 text-center py-2 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
      >
        Explore All Groups
      </Link>
    </div>
  );
}
